import React, { useState } from 'react';
import { useAppSelector, useTranslate } from '@src/core/hooks';
import Input from '@src/components/ui2/Input';
import { authSelector, buyerIsForeign, buyerNeedsPhoneVerificationSelector } from '@src/features/auth';
import UserForm from '../UserForm';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { MdArrowForward, MdOutlineEmail } from 'react-icons/md';
import useApiClient from '@src/hooks/useApiClient';
import LoginForm from '../LoginForm';
import Button from '@src/components/ui2/Button';
import {
  AuthMessage,
  EmailDisplay,
  PhoneVerificationDisplay,
  FormContainer,
  TitleAndDescriptionWrapper,
} from './style';
import { zodResolver } from '@hookform/resolvers/zod';
import { schema } from './schema';
import PhoneVerification from '@src/components/reusable/PhoneVerification';

type AuthFormProps = {
  editUser?: boolean;
  fillCustomerForm?: boolean;
  onLoginSuccess?: () => void;
};

const AuthForm = (props: AuthFormProps) => {
  const { editUser = false, fillCustomerForm = false, onLoginSuccess = () => {} } = props;

  const { i18n } = useTranslate();
  const { bearerAuth, buyers } = useApiClient();

  const { logged, buyer } = useAppSelector(authSelector);
  const needsPhoneVerification = useAppSelector(buyerNeedsPhoneVerificationSelector);

  const [loadingCheckEmail, setLoadingCheckEmail] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [emailAvailable, setEmailAvailable] = useState<boolean | undefined>(undefined);

  const { handleSubmit, control, formState } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: zodResolver(schema(i18n)),
  });
  const { errors } = formState;

  const checkEmail = async (payload: FieldValues) => {
    setLoadingCheckEmail(true);

    try {
      const { data } = await buyers.checkEmail(payload['email'], {
        ...bearerAuth,
      });
      setLoadingCheckEmail(false);

      setEmailAvailable(!data.available);
      setEmail(payload['email']);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingCheckEmail(false);
    }
  };

  return (
    <>
      {!logged && !email ? (
        <form onSubmit={handleSubmit(checkEmail)}>
          <AuthMessage>{i18n('direct_link_auth_message')}</AuthMessage>

          <Controller
            name='email'
            control={control}
            render={({ field: { onChange, value, name, ...rest } }) => (
              <Input
                type='email'
                label={i18n('direct_link_auth_email_label')}
                name={name}
                value={value || ''}
                required
                onChange={onChange}
                error={!!errors[name]}
                disabled={loadingCheckEmail}
                helperText={errors[name]?.message as string}
                {...rest}
              />
            )}
          />

          <Button fullWidth type='submit' loading={loadingCheckEmail}>
            {i18n('direct_link_auth_continue')} <MdArrowForward />
          </Button>
        </form>
      ) : null}

      {!logged && email ? (
        <>
          <EmailDisplay>
            <MdOutlineEmail /> {email}
          </EmailDisplay>

          {emailAvailable === true ? <LoginForm email={email} onSuccess={onLoginSuccess} /> : null}

          {emailAvailable === false ? <UserForm /> : null}
        </>
      ) : null}

      {logged && buyer && !buyer.phoneVerifiedAt && needsPhoneVerification && !editUser ? (
        <>
          <EmailDisplay>
            <MdOutlineEmail /> {buyer!.email}
          </EmailDisplay>

          <PhoneVerificationDisplay>
            <PhoneVerification.Form
              render={({ form }) => (
                <FormContainer>
                  <TitleAndDescriptionWrapper>
                    <PhoneVerification.Title />

                    <PhoneVerification.Description />
                  </TitleAndDescriptionWrapper>

                  <div style={{ marginBottom: '-8px' }}>
                    <PhoneVerification.Input form={form} />
                  </div>

                  <PhoneVerification.Message />

                  <PhoneVerification.SubmitButton />

                  <PhoneVerification.ResendCode />
                </FormContainer>
              )}
            />
          </PhoneVerificationDisplay>
        </>
      ) : null}

      {logged && editUser ? <UserForm fillData={fillCustomerForm} /> : null}
    </>
  );
};

export default AuthForm;
